<template>
  <!-- One multi-select filter dropdown. Lives here rather than inline in
       Support_Hades so every filter declared in hades_filters.js renders the
       same control — a per-filter <select> is how one of four dropdowns ends up
       behaving differently from the other three. -->
  <div ref="root" class="relative">
    <button
      type="button"
      class="flex items-center gap-1.5 h-[30px] px-2.5 rounded border text-xs"
      :class="modelValue.length
        ? 'border-primary-500 bg-primary-100 text-primary-700 font-semibold'
        : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'"
      @click="open = !open"
    >
      {{ label }}
      <span
        v-if="modelValue.length"
        class="text-[11px] font-normal"
        :class="{ sensitive: summarySensitive }"
      >
        {{ summary }}
      </span>
      <span class="text-[9px] leading-none text-gray-600">▾</span>
    </button>

    <div
      v-if="open"
      class="absolute z-20 mt-1 min-w-[200px] max-h-64 overflow-y-auto
             bg-white border border-gray-200 rounded shadow-lg py-1"
    >
      <p v-if="!options.length" class="px-3 py-2 text-xs text-gray-600">
        No values on the board.
      </p>
      <label
        v-for="option in options"
        :key="option.value"
        class="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-800
               hover:bg-gray-50 cursor-pointer select-none"
      >
        <input
          type="checkbox"
          class="w-3 h-3 accent-primary-500"
          :checked="modelValue.includes(option.value)"
          @change="toggle(option.value)"
        />
        <span
          class="flex-1 truncate"
          :class="{ sensitive: optionSensitive(option.value) }"
        >{{ option.label }}</span>
        <!-- Counts are faceted (every other filter applied), so a 0 means
             "picking this gives you nothing", not "this value does not exist". -->
        <span class="text-gray-600">{{ option.count }}</span>
      </label>
      <button
        v-if="modelValue.length"
        type="button"
        class="w-full text-left px-3 py-1.5 text-xs text-primary-700 hover:bg-gray-50"
        @click="$emit('update:modelValue', [])"
      >Clear</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HadesFilterSelect',
  props: {
    label: { type: String, required: true },
    // [{ value, label, count }], already volume-ordered by filterOptions().
    options: { type: Array, required: true },
    modelValue: { type: Array, default: () => [] },
    // (value) => boolean. True for an option whose value identifies a person
    // or customer, so Demo Mode masks it. From the filter def's `sensitive`.
    sensitive: { type: Function, default: null },
  },
  emits: ['update:modelValue'],
  data() {
    return { open: false };
  },
  computed: {
    summary() {
      if (this.modelValue.length === 1) {
        const hit = this.options.find((o) => o.value === this.modelValue[0]);
        // A selected value can outlive its option for one tick — the parent's
        // watcher drops it on the next — so fall back to the raw value rather
        // than rendering "undefined".
        return hit ? hit.label : String(this.modelValue[0]);
      }
      return `${this.modelValue.length}`;
    },
    // Only a single selection prints a value; "2" is a count.
    summarySensitive() {
      return this.modelValue.length === 1 && this.optionSensitive(this.modelValue[0]);
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
  },
  methods: {
    optionSensitive(value) {
      return !!this.sensitive && this.sensitive(value);
    },
    // NOTHING IN HERE CALLS .stop, deliberately. The obvious guard on the
    // toggle — @click.stop so the document listener does not close the panel it
    // just opened — is unnecessary, because the button is inside `root` and the
    // check below already exempts it. Worse, it also stopped every OTHER
    // instance's listener from firing, so opening the second dropdown left the
    // first one open and the two absolutely-positioned panels overlapped.
    onDocClick(event) {
      if (this.open && !this.$refs.root?.contains(event.target)) this.open = false;
    },
    // Emits a NEW array. Mutating the prop would write straight into the
    // parent's filter state and skip the watcher that keeps the URL and the
    // stored state in step with it.
    toggle(value) {
      const next = this.modelValue.includes(value)
        ? this.modelValue.filter((v) => v !== value)
        : [...this.modelValue, value];
      this.$emit('update:modelValue', next);
    },
  },
};
</script>
